/* ==========================================================================
   Wattec — Support hub behaviour

   Everything here is progressive enhancement. If this file fails to load the
   page still works: the FAQ accordions are real <details> elements, every
   answer is already in the page source, and the contact details are plain
   links. Only the search, filtering, open/closed badge and the animations
   come from here.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------- reveal */
  function initReveal(root) {
    var items = root.querySelectorAll('.wt-reveal');
    if (!items.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      for (var i = 0; i < items.length; i++) items[i].classList.add('is-in');
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    for (var j = 0; j < items.length; j++) observer.observe(items[j]);
  }

  /* ------------------------------------------------------------- accordion */
  // <details> hides its contents outright when closed, so an open/close
  // animation has to keep the element open until the collapse has finished.
  function initAccordion(root) {
    var items = root.querySelectorAll('.wt-faq-item');

    Array.prototype.forEach.call(items, function (item) {
      if (reduceMotion) return;
      item.classList.add('wt-anim');

      var summary = item.querySelector('.wt-faq-item__q');
      if (!summary) return;

      summary.addEventListener('click', function (event) {
        event.preventDefault();
        if (item.dataset.busy === '1') return;

        var group = item.closest('[data-faq-root]');
        var exclusive = group && group.dataset.exclusive === 'true';

        if (!item.open) {
          if (exclusive) {
            Array.prototype.forEach.call(
              group.querySelectorAll('.wt-faq-item[open]'),
              function (other) {
                if (other !== item) close(other);
              }
            );
          }
          item.open = true;
        } else {
          close(item);
        }
      });
    });

    function close(item) {
      var body = item.querySelector('.wt-faq-item__body');
      if (!body) {
        item.open = false;
        return;
      }
      item.dataset.busy = '1';
      // Force a frame at the open height so the transition has somewhere to go.
      body.style.gridTemplateRows = '1fr';
      requestAnimationFrame(function () {
        body.style.gridTemplateRows = '0fr';
        var done = function () {
          body.style.gridTemplateRows = '';
          item.open = false;
          delete item.dataset.busy;
          body.removeEventListener('transitionend', done);
        };
        body.addEventListener('transitionend', done);
        window.setTimeout(done, 500); // safety net if transitionend never fires
      });
    }
  }

  /* -------------------------------------------------------- search + chips */
  function initFaq(root) {
    var faqRoot = root.querySelector('[data-faq-root]');
    if (!faqRoot) return;

    var input = faqRoot.querySelector('[data-faq-input]');
    var clear = faqRoot.querySelector('[data-faq-clear]');
    var chips = faqRoot.querySelectorAll('[data-faq-chip]');
    var items = faqRoot.querySelectorAll('.wt-faq-item');
    var groups = faqRoot.querySelectorAll('[data-faq-group]');
    var empty = faqRoot.querySelector('[data-faq-empty]');
    var emptyTerm = faqRoot.querySelector('[data-faq-empty-term]');
    var liveRegion = faqRoot.querySelector('[data-faq-live]');

    var activeCategory = 'all';
    var term = '';

    function normalise(value) {
      return (value || '').toLowerCase().replace(/\s+/g, ' ').trim();
    }

    // Wrap matches in the question text only — rewriting answer HTML risks
    // breaking the links and lists inside it.
    function highlight(item) {
      var label = item.querySelector('[data-faq-label]');
      if (!label) return;

      var original = label.dataset.original || label.textContent;
      label.dataset.original = original;

      if (!term) {
        label.textContent = original;
        return;
      }

      var index = normalise(original).indexOf(term);
      if (index === -1) {
        label.textContent = original;
        return;
      }

      label.textContent = '';
      label.appendChild(document.createTextNode(original.slice(0, index)));
      var mark = document.createElement('mark');
      mark.className = 'wt-mark';
      mark.textContent = original.slice(index, index + term.length);
      label.appendChild(mark);
      label.appendChild(document.createTextNode(original.slice(index + term.length)));
    }

    function apply() {
      var visible = 0;

      Array.prototype.forEach.call(items, function (item) {
        var haystack = normalise(item.dataset.search);
        var inCategory =
          activeCategory === 'all' || item.dataset.category === activeCategory;
        var matches = !term || haystack.indexOf(term) !== -1;
        var show = inCategory && matches;

        item.hidden = !show;
        if (show) {
          visible += 1;
          highlight(item);
        } else if (item.open) {
          item.open = false;
        }
      });

      // A category heading only earns its place if something under it survived.
      Array.prototype.forEach.call(groups, function (group) {
        var key = group.dataset.faqGroup;
        var any = faqRoot.querySelector(
          '.wt-faq-item[data-category="' + key + '"]:not([hidden])'
        );
        group.hidden = !any;
      });

      if (empty) empty.classList.toggle('is-visible', visible === 0);
      if (emptyTerm) emptyTerm.textContent = input ? input.value : '';
      if (clear) clear.classList.toggle('is-visible', !!term);

      if (liveRegion) {
        liveRegion.textContent =
          visible === 0
            ? 'No questions matched your search.'
            : visible + (visible === 1 ? ' question' : ' questions') + ' shown.';
      }
    }

    if (input) {
      var debounce;
      input.addEventListener('input', function () {
        window.clearTimeout(debounce);
        debounce = window.setTimeout(function () {
          term = normalise(input.value);
          apply();
        }, 120);
      });

      // Escape clears, which is what people expect from a search field.
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Escape' && input.value) {
          input.value = '';
          term = '';
          apply();
        }
      });
    }

    if (clear) {
      clear.addEventListener('click', function () {
        if (!input) return;
        input.value = '';
        term = '';
        apply();
        input.focus();
      });
    }

    Array.prototype.forEach.call(chips, function (chip) {
      chip.addEventListener('click', function () {
        activeCategory = chip.dataset.faqChip || 'all';
        Array.prototype.forEach.call(chips, function (other) {
          other.setAttribute('aria-selected', other === chip ? 'true' : 'false');
        });
        apply();

        // Keep the chosen chip in view inside the sideways-scrolling strip.
        if (chip.scrollIntoView) {
          chip.scrollIntoView({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      });
    });

    // Support /pages/support#faq-warranty style deep links from anywhere.
    function openFromHash() {
      var hash = window.location.hash;
      if (!hash || hash.length < 2) return;
      var target = faqRoot.querySelector(hash.replace(/[^#\w-]/g, ''));
      if (!target || !target.classList.contains('wt-faq-item')) return;
      target.hidden = false;
      target.open = true;
      window.setTimeout(function () {
        target.scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth',
          block: 'center'
        });
      }, 200);
    }

    window.addEventListener('hashchange', openFromHash);
    openFromHash();
    apply();
  }

  /* ---------------------------------------------------------- opening hours */
  // Worked out in the shop's own timezone so a customer abroad still sees the
  // right answer, rather than "open" because it is 11am where they are.
  function initHours(root) {
    var holder = root.querySelector('[data-hours]');
    if (!holder) return;

    var schedule;
    try {
      schedule = JSON.parse(holder.textContent);
    } catch (error) {
      return;
    }
    if (!schedule || !schedule.days) return;

    var zone = schedule.timezone || 'Europe/London';
    var now;
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: zone,
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      }).formatToParts(new Date());

      now = parts.reduce(function (acc, part) {
        acc[part.type] = part.value;
        return acc;
      }, {});
    } catch (error) {
      return;
    }

    var order = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    var todayIndex = order.indexOf(now.weekday);
    if (todayIndex === -1) return;

    var minutes = parseInt(now.hour, 10) * 60 + parseInt(now.minute, 10);

    function isOpen(ranges) {
      if (!ranges || !ranges.length) return false;
      for (var i = 0; i < ranges.length; i++) {
        var from = ranges[i][0];
        var to = ranges[i][1];
        if (minutes >= from && minutes < to) return true;
      }
      return false;
    }

    Object.keys(schedule.days).forEach(function (channel) {
      var week = schedule.days[channel];
      var open = isOpen(week[todayIndex]);

      var badges = root.querySelectorAll('[data-status="' + channel + '"]');
      Array.prototype.forEach.call(badges, function (badge) {
        badge.hidden = false;
        badge.classList.add(open ? 'wt-status--open' : 'wt-status--closed');
        badge.textContent = open
          ? badge.dataset.openLabel || 'Open now'
          : badge.dataset.closedLabel || 'Closed now';
      });
    });

    // A row can cover a range ("Monday – Friday"), so check every day it lists
    // rather than only the first one.
    var rows = root.querySelectorAll('[data-day-numbers]');
    Array.prototype.forEach.call(rows, function (row) {
      var covered = (row.dataset.dayNumbers || '').split(',');
      for (var i = 0; i < covered.length; i++) {
        if (parseInt(covered[i], 10) === todayIndex) {
          row.classList.add('is-today');
          return;
        }
      }
    });
  }

  /* ----------------------------------------------------------- stat counter */
  function initStats(root) {
    var stats = root.querySelectorAll('[data-count-to]');
    if (!stats.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          countUp(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    Array.prototype.forEach.call(stats, function (stat) {
      observer.observe(stat);
    });

    function countUp(el) {
      var target = parseFloat(el.dataset.countTo);
      if (isNaN(target)) return;
      var prefix = el.dataset.prefix || '';
      var suffix = el.dataset.suffix || '';
      var start = null;
      var duration = 1400;

      function frame(timestamp) {
        if (start === null) start = timestamp;
        var progress = Math.min((timestamp - start) / duration, 1);
        var eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = prefix + Math.round(target * eased) + suffix;
        if (progress < 1) requestAnimationFrame(frame);
      }

      requestAnimationFrame(frame);
    }
  }

  /* -------------------------------------------------------------- bootstrap */
  function init(scope) {
    var roots = (scope || document).querySelectorAll('.wt-support');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.wtReady === '1') return;
      root.dataset.wtReady = '1';
      initReveal(root);
      initAccordion(root);
      initFaq(root);
      initHours(root);
      initStats(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-run when a section is added or reloaded in the theme customiser.
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (target && target.dataset) delete target.dataset.wtReady;
    init(event.target);
  });
})();
